import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppContext } from "./components/context/Context";
import { LoadingEllipsis } from "./components/loading/Loading";
import { useAuth, withAuthenticationRequired } from "react-oidc-context";
import ErrorBoundary from "./components/error/ErrorBoundary";
import { observer } from "mobx-react-lite";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  type ReactElement,
} from "react";
import { ToastContainer } from "react-toastify";
import MainApp from "./components/store/App";

const LoginPage = lazy(() => import("./loggedout/LoginPage"));
const LoggedIn = lazy(() => import("./loggedin/LoggedIn"));
const Queries = lazy(() => import("./loggedin/queries/Queries"));
const Licensing = lazy(() => import("./loggedin/licensing/Licensing"));
interface IProps {
  children: ReactElement;
}

const PrivateRoute = (props: IProps) => {
  const { children } = props;
  return children;
};

const AuthRedirect = () => <Navigate to="/user/queries" />;

const PrivateRouteWithAuth = withAuthenticationRequired(PrivateRoute, {
  OnRedirecting: () => <AuthRedirect />,
});

const PrivateLoggedIn = () => {
  return (
    <Suspense fallback={<LoadingEllipsis fullHeight />}>
      <PrivateRouteWithAuth>
        <LoggedIn />
      </PrivateRouteWithAuth>
    </Suspense>
  );
};

const AppRoutes = observer(() => {
  const auth = useAuth();

  useEffect(() => {
    const onAccessTokenExpiring = async () => {
      try {
        await auth.signinSilent().then((user) => {
          if (user) {
            sessionStorage.setItem("access_token", user.access_token);
          } else {
            console.log("signing out...");
            auth.signoutRedirect();
          }
        });
      } catch (error) {
        auth.signoutSilent();
      }
    };

    auth.events.addAccessTokenExpiring(onAccessTokenExpiring);

    if (auth.user?.access_token) {
      sessionStorage.setItem("access_token", auth.user.access_token);
    }

    return () => {
      auth.events.removeAccessTokenExpiring(onAccessTokenExpiring);
    };
  }, [auth]);

  if (auth.isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/user/queries" />} />
          <Route path="*" element={<Navigate to="/user/queries" />} />
          <Route path="/user" element={<PrivateLoggedIn />}>
            <Route
              path="queries"
              element={
                <Suspense fallback={<LoadingEllipsis fullHeight />}>
                  <Queries />
                </Suspense>
              }
            />
            <Route
              path={"licenses/:queryId"}
              element={
                <Suspense fallback={<LoadingEllipsis fullHeight />}>
                  <Licensing />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    );
  }

  if (auth.isLoading) {
    return (
      <div>
        <LoadingEllipsis fullHeight />
      </div>
    );
  }

  if (auth.error) {
    return (
      <ErrorBoundary>
        <div
          className="uk-section uk-section-muted uk-flex uk-flex-middle uk-animation-fade"
          data-uk-height-viewport
        >
          <div className="uk-container ">
            <div className="uk-border-rounded uk-width-large uk-margin-auto uk-card uk-card-default uk-card-body btn-primary">
              <p className="uk-text-center uk-text-emphasis">
                An error occured! Please try again later.
              </p>
              <p className="uk-text-center uk-text-emphasis">Error:</p>
              <p className="uk-text-center uk-text-emphasis">
                {auth.error.message}
                <br />
                {String(auth.error.name)}
              </p>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="*" element={<Navigate to="/" />} />
        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingEllipsis fullHeight />}>
              <LoginPage />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
});

const App = () => {
  const appRef = useRef<MainApp | null>(null);
  if (!appRef.current) {
    appRef.current = new MainApp();
  }
  const { store, api } = appRef.current;

  return (
    <AppContext.Provider value={{ store, api }}>
      <AppRoutes />
      <ToastContainer />
    </AppContext.Provider>
  );
};
export default App;
